/** @file SettingsScreen: server, general and default options, shortcut tables with overrides, cheat sheet. */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { applyOverrides } from '@zilo/pose3d-shortcuts/applyOverrides';
import { chordLabel } from '@zilo/pose3d-shortcuts/chordLabel';
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import { bindingLabel } from '../shell/bindingLabel';
import { EDITOR_SETTINGS } from '../shell/editorSettings';
import { heldKeys } from '../shell/heldKeys';
import { PLATFORM } from '../shell/platform';
import type { ShellProps } from '../shell/Shell';
import { useApi } from '../shell/useApi';
import { signOut } from '../shell/signOut';
import { useEditorStore } from '../state/useEditorStore';
import { Button } from '../ui-kit/Button';
import { Field } from '../ui-kit/Field';
import { Kbd } from '../ui-kit/Kbd';
import { Panel } from '../ui-kit/Panel';
import { ChordCapture } from './ChordCapture';
import { tileBindingLabels } from './tile/tileBindingLabels';
import { DefaultsSection } from './DefaultsSection';
import { GeneralSection } from './GeneralSection';

const CHEAT_SHEET: (readonly [string, string])[] = [
  ['Q W E R T', 'Hand, Move, Rotate, Scale, IK tools'], ['X', 'Local / World gizmo axes'], ['Z', 'Pivot / Center'],
  ['1 2 3', 'Pose, Art, Agent screens'], ['Ctrl/Cmd+Z', 'Undo (server; a Tile tab undoes its own strokes)'], ['Left click', 'Select'],
  ['Double-click a slot card', 'Paint that tile in a Tile tab (B E G I L tools, M mirror, O onion, Ctrl+S Save, Alt+W close)'],
  ['Right click', 'Context menu (everywhere; also the Menu key or Shift+F10)'], ...heldKeys,
];

/** Server URL (applied on reload, empty = this page's origin) and sign-out. */
function ServerSection(): ReactElement {
  const client = useApi();
  const store = useEditorStore();
  const [url, setUrl] = useState(EDITOR_SETTINGS.url.load);
  return (
    <Panel title="Server">
      <div className="p3-stack" style={{ padding: 12 }}>
        <Field label="Server URL (empty = this page; applies after reload)">
          <input className="p3-input p3-mono" value={url} placeholder={client?.url ?? 'memory://'} onChange={(e) => { setUrl(e.target.value); }} />
        </Field>
        <div className="p3-row">
          <Button variant="primary" onClick={() => { EDITOR_SETTINGS.url.save(url.trim() === '' ? null : url.trim()); }}>Save URL</Button>
          <Button onClick={() => { globalThis.location.reload(); }}>Reload</Button>
          <Button variant="danger" disabled={client === null} onClick={() => { signOut(store, client); }}>Sign out</Button>
        </div>
      </div>
    </Panel>
  );
}

/** One table: effective chord, context and id per binding, with a capture field to override it. */
function BindingTable({ table, overrides, onOverrides }: { table: Binding[] } & Pick<ShellProps, 'overrides' | 'onOverrides'>): ReactElement {
  const { table: effective, dropped } = applyOverrides(table, overrides, PLATFORM);
  const set = (b: Binding, keys: string): void => {
    onOverrides([...overrides.filter((o) => o.id !== b.id || o.context !== b.context), { id: b.id, context: b.context, keys }]);
  };
  return (
    <>
      {dropped.map((d) => (
        <tr key={`dropped-${d.context}/${d.id}`}>
          <td colSpan={4} className="p3-error">Override {d.keys} for {d.id} was refused (reserved or clashing).</td>
        </tr>
      ))}
      {effective.map((b) => (
        <tr key={`${b.context}/${b.id}`}>
          <td><Kbd>{chordLabel(b.keys, PLATFORM)}</Kbd></td>
          <td className="p3-mono p3-muted">{b.context}</td>
          <td title={b.id}>{tileBindingLabels[b.id] ?? bindingLabel(b.id)}</td>
          <td><ChordCapture label={b.id} onChord={(keys) => { set(b, keys); }} /></td>
        </tr>
      ))}
    </>
  );
}

/** Settings: server, general, defaults, shortcuts (every table the shell was given) with Reset all, and the panels and tools cheat sheet. */
export function SettingsScreen({ bindingTables, overrides, onOverrides }: ShellProps): ReactElement {
  return (
    <div className="p3-screen p3-stack">
      <div className="p3-screen-head"><h1>Settings</h1></div>
      <ServerSection />
      <GeneralSection />
      <DefaultsSection />
      <Panel title="Shortcuts" actions={<Button onClick={() => { onOverrides([]); }} disabled={overrides.length === 0}>Reset all</Button>}>
        <table className="p3-table" aria-label="Shortcuts">
          <thead><tr><th>Keys</th><th>Context</th><th>Command</th><th /></tr></thead>
          <tbody>{bindingTables.map((t, i) => <BindingTable key={String(i)} table={t} overrides={overrides} onOverrides={onOverrides} />)}</tbody>
        </table>
      </Panel>
      <Panel title="Panels & tools">
        <table className="p3-table" aria-label="Cheat sheet">
          <tbody>{CHEAT_SHEET.map(([keys, what]) => <tr key={keys}><td><Kbd>{keys}</Kbd></td><td>{what}</td></tr>)}</tbody>
        </table>
      </Panel>
    </div>
  );
}
